import { ProcessTypes, ProcessItemTypes } from "../../utils/icons";
import { i18n } from "../../main";
import { Direction, HotkeyScopes, Positions, Preferences, TreeDefaultNodeIds } from "../../utils/constants";
import { compareNodes } from "../../utils/util";
import api from "../api";
import { v4 as uuidv4 } from "uuid";
import { navigateTo } from "../../router";

export class RedZoneHelper {
  constructor({ state, dispatch, getters }) {
    this.state = state;
    this.dispatch = dispatch;
    this.getters = getters;
  }
  async addProcess({ id, type = ProcessTypes.Normal, design }) {
    try {
      if (this.state.isLoading) return;
      this.state.isLoading = true;

      if (!id) {
        id = TreeDefaultNodeIds.Private;
      }
      const parent = this.state.tree.getNode(this.state.tree.allData, id);
      let teamId = [...parent.parents, id][1];

      const result = await this.dispatch("pdesigns/create", { type, teamId, design }, { root: true });
      const business = result.data;
      const newNodeId = uuidv4();
      this.state.tree.addNode(
        {
          itemId: business.id,
          id: newNodeId,
          title: business.title,
          expanded: true,
          type,
          children: [],
        },
        id
      );
      const root = {
        id: "ROOT",
        title: "ROOT",
        type: "folder",
        expanded: true,
        children: [
          {
            itemId: business.id,
            id: uuidv4(),
            title: i18n.global.tm("sidebar.sheets.title"),
            type: TreeDefaultNodeIds.Sheets,
            expanded: true,
            children: [
              {
                id: uuidv4(),
                title: "Sheet1",
                type: ProcessItemTypes.Sheet,
                expanded: true,
                children: [],
              },
            ],
          },
        ],
      };
      const defaultSheet = { sheets: root, designId: business.id };
      await api.post("sheets", { ...defaultSheet });

      await this.dispatch("recalculateHashTable");
      this.dispatch("calculateVisibleItemIds");
      this.dispatch("setActiveItem", { id: newNodeId, itemId: business.id, editMode: true });
      await this.dispatch("persist");
      this.dispatch("open", newNodeId);
      this.dispatch("smartSearch/loadProcesses", null, { root: true });
      this.state.isLoading = false;
    } catch (error) {
      console.log(error);
      this.state.isLoading = false;
    }
  }
  addFolder(id) {
    if (this.state.isLoading) return;
    this.state.isLoading = true;

    const nodeId = uuidv4();
    this.state.tree.addNode(
      {
        id: nodeId,
        itemId: nodeId,
        title: "New Folder",
        expanded: true,
        type: ProcessItemTypes.Folder,
        children: [],
      },
      id
    );
    this.dispatch("recalculateHashTable");
    this.dispatch("setActiveItem", { id, editMode: false });
    this.dispatch("calculateVisibleItemIds");
    this.dispatch("persist");
    this.state.isLoading = false;
  }
  async addGrid(id) {
    if (this.state.isLoading) return;
    this.state.isLoading = true;
    try {
      const parent = this.state.tree.getNode(this.state.tree.allData, id);
      let teamId = [...parent.parents, id][1];
      const result = await this.dispatch("pdesigns/createGrid", { type: ProcessTypes.Grid, teamId }, { root: true });
      const newNodeId = uuidv4();
      this.state.tree.addNode(
        {
          itemId: result.data.id,
          id: newNodeId,
          title: result.data.title,
          expanded: true,
          type: ProcessTypes.Grid,
          children: [],
        },
        id
      );
      // create a default sheet.
      const root = {
        id: "ROOT",
        title: "ROOT",
        type: "folder",
        expanded: true,
        children: [
          {
            itemId: result.data.id,
            id: uuidv4(),
            title: i18n.global.tm("sidebar.sheets.title"),
            type: TreeDefaultNodeIds.Sheets,
            expanded: true,
            children: [
              {
                id: uuidv4(),
                title: "Sheet1",
                type: ProcessItemTypes.Sheet,
                expanded: true,
                children: [],
              },
            ],
          },
        ],
      };
      const defaultSheet = { sheets: root, designId: result.data.id };
      await api.post("sheets", { ...defaultSheet });

      this.dispatch("recalculateHashTable");
      this.dispatch("setActiveItem", { id: newNodeId, itemId: result.data.id, editMode: true });
      this.dispatch("calculateVisibleItemIds");
      await this.dispatch("persist");
      this.dispatch("open", newNodeId);
      this.dispatch("smartSearch/loadGrids", null, { root: true });
      this.state.isLoading = false;
    } catch (error) {
      console.log(error);
      this.state.isLoading = false;
    }
  }
  async remove({ ids }) {
    // reset formulaBox in case it was opened.
    this.dispatch("pdesign/toggleFormulaBox", false, { root: true });

    if (ids.length > 1) return this.dispatch("removePermanently", { ids });
    const id = ids[0];
    try {
      this.state.isLoading = true;
      const node = { ...this.state.tree.getNode(this.state.tree.allData, id) };
      if (node.level === 1) return;
      this.state.tree.addReference(node);
      const team = this.state.tree.getNode(this.state.tree.allData, node.parents[1]);

      // Generate the path using the reference or parents
      const pathParts = node.parents
        .map((parentId) => {
          const parentNode = this.state.tree.getNode(this.state.tree.allData, parentId);
          return parentNode?.title || ""; // Use title if available
        })
        .filter((title) => title.toLowerCase() !== "root"); // Remove "root" from the path

      node.path = pathParts;

      if (node.children.length > 0)
        return this.dispatch(
          "addToast",
          {
            severity: "error",
            life: 3000,
            summary: "Failed To Delete",
            detail: "Sorry You can only delete an empty business",
          },
          { root: true }
        );
      // if deleted item is in the trash, delete permanently
      if (team.id === TreeDefaultNodeIds.Trash) return this.dispatch("removePermanently", { ids });

      if (node.type === ProcessItemTypes.Folder) {
        this.state.tree.removeNode(id);
        this.dispatch("resetActiveItem");
      } else {
        const subTrashNodeId = `TRASH_${team.id}`;
        const subTrashNode = this.state.tree.getNode(this.state.tree.allData, subTrashNodeId);
        if (!subTrashNode) {
          this.state.tree.addNode(
            {
              id: `TRASH_${team.id}`,
              title: team.title,
              expanded: false,
              type: ProcessItemTypes.Folder,
              children: [],
            },
            TreeDefaultNodeIds.Trash
          );
        }

        this.dispatch("resetActiveItem");
        this.state.tree.removeNode(id);
        this.state.tree.addNode(node, subTrashNodeId);
      }

      this.dispatch(
        {
          [ProcessItemTypes.Grid]: "smartSearch/loadGrids",
          [ProcessItemTypes.Posting]: "smartSearch/loadProcesses",
          [ProcessItemTypes.Normal]: "smartSearch/loadProcesses",
        }[node.type] || "smartSearch/loadPlayers",
        null,
        { root: true }
      );

      this.dispatch("calculateVisibleItemIds");
      this.dispatch("recalculateHashTable");
      this.dispatch("persist", node.reference[1]);
      this.dispatch(
        "addToast",
        {
          severity: "success",
          life: 3000,
          summary: "Deleted Successfully",
          detail:
            node.type === ProcessItemTypes.Folder
              ? "Folder deleted Successfully."
              : "Process Deleted Successfully but you can restore from trash.",
        },
        { root: true }
      );
    } catch (error) {
      this.dispatch(
        "addToast",
        {
          severity: "error",
          life: 3000,
          summary: "Failed To Delete",
          detail: "Sorry Something went wrong",
        },
        { root: true }
      );
      console.log(error);
    } finally {
      this.state.isLoading = false;
    }
  }
  async removePermanently({ ids }) {
    const teamIds = [];
    try {
      ids.forEach((id) => {
        this.state.isLoading = true;
        const node = { ...this.state.tree.getNode(this.state.tree.allData, id) };
        if (node.level === 1) return;

        this.state.tree.addReference(node);
        this.state.tree.removeNode(id);

        // remove empty trash can
        const trashTeamNode = this.state.tree.getNode(this.state.tree.allData, node.parents[2]);
        if (trashTeamNode?.children.length === 0) this.state.tree.removeNode(node.parents[2]);
        // store team id.
        !teamIds.includes(node.reference[1]) && teamIds.push(node.reference[1]);
      });
      this.dispatch("calculateVisibleItemIds");
      this.dispatch("recalculateHashTable");
      this.dispatch("persist", teamIds);
      this.dispatch(
        "addToast",
        {
          severity: "success",
          life: 3000,
          summary: "Deleted Successfully",
          detail: "Your data has been deleted permanently.",
        },
        { root: true }
      );
    } catch (error) {
      this.dispatch(
        "addToast",
        {
          severity: "error",
          life: 3000,
          summary: "Failed To Delete",
          detail: "Sorry Something went wrong",
        },
        { root: true }
      );
      console.log(error);
    } finally {
      this.state.isLoading = false;
    }
  }
  restore({ ids }) {
    const teamIds = [];
    ids.forEach((id) => {
      const node = this.state.tree.getNode(this.state.tree.allData, id);
      const trashTeamId = node.parents[2];
      const lastParentId = node.reference[node.reference.length - 1];
      const lastParent = this.state.tree.getNode(this.state.tree.allData, lastParentId);

      // if original return point doesn't exist or is in trash itself then return to child of main team
      const returnPoint =
        lastParent && lastParent.parents[1] !== TreeDefaultNodeIds.Trash ? lastParentId : node.reference[1];

      this.state.tree.setValue(id, "reference", null);
      this.state.tree.moveNode(id, Positions.Center, returnPoint);

      // remove empty trash can
      const trashTeamNode = this.state.tree.getNode(this.state.tree.allData, trashTeamId);
      !teamIds.includes(node.reference[1]) && teamIds.push(node.reference[1]);

      if (trashTeamNode?.children.length === 0) {
        this.state.tree.removeNode(trashTeamId);
      }
    });
    this.dispatch("resetActiveContextMenu", null, { root: true });
    this.dispatch("calculateVisibleItemIds");
    this.dispatch("recalculateHashTable");
    this.dispatch("persist", teamIds);
  }
  open(id) {
    const item = this.state.tree.getNode(this.state.tree.allData, id);
    if (!["normal", "posting", "grid", "cycle"].includes(item.type)) return;

    navigateTo({ path: `/designs/${item.itemId}` });
    this.dispatch("resetActiveContextMenu", null, { root: true });
    this.dispatch("pdesign/setOpenedItem", { id, itemId: item.itemId }, { root: true });
  }
  async rename({ id, itemId, title, basePath }) {
    try {
      if (this.state.isLoading) return;
      this.state.isLoading = true;

      this.state.tree.setValue(id, "title", title);

      // reset process getting edited
      this.dispatch("setActiveItem", { id, itemId, editMode: false });
      this.dispatch("setScope", HotkeyScopes.Sidebar.Processes, { root: true });
      const teamId = this.getters["teamId"](id);
      this.dispatch("renameNode", { nodeId: id, newTitle: title, basePath });
      this.dispatch("cells/persistProperty", { key: "title", value: title, designId: itemId }, { root: true });
      this.dispatch("persist", teamId);
      this.dispatch("recalculateHashTable");

      const node = this.state.tree.getNode(this.state.tree.allData, id);
      this.dispatch(
        {
          [ProcessItemTypes.Grid]: "smartSearch/loadGrids",
          [ProcessItemTypes.Posting]: "smartSearch/loadProcesses",
          [ProcessItemTypes.Normal]: "smartSearch/loadProcesses",
        }[node.type] || "smartSearch/loadPlayers",
        null,
        { root: true }
      );

      this.state.isLoading = false;
    } catch (error) {
      console.log(error);
      this.state.isLoading = false;
    }
  }
  async renameTeam({ id, itemId, title }) {
    try {
      this.state.tree.setValue(itemId, "title", title);

      // reset process getting edited
      this.dispatch("setActiveItem", { id, itemId, editMode: false });

      await this.dispatch("persist", itemId);
    } catch (error) {
      console.log(error);
    }
  }
  moveNode({ id, direction }) {
    if (!this.getters.isMoveDownAllowed(id)) return;
    if (direction === Direction.Up) this.state.tree.moveNodeUp(id);
    else this.state.tree.moveNodeDown(id);

    this.dispatch("calculateVisibleItemIds");
    this.dispatch("resetActiveContextMenu", null, { root: true });
    this.dispatch("persist");
  }
  moveNodeOneLevel({ id, direction }) {
    const isAllowed =
      direction === Direction.Up ? this.getters.isMoveUpAllowed(id) : this.getters.isMoveDownAllowed(id);
    if (!isAllowed) return;
    if (direction === Direction.Up) this.state.tree.moveNodeOneLevelUp(id);
    else this.state.tree.moveNodeOneLevelDown(id);

    this.dispatch("calculateVisibleItemIds");
    this.dispatch("resetActiveContextMenu", null, { root: true });
    this.dispatch("persist");
  }
  toggleExpanded({ id, itemId, expanded = null }) {
    const item = this.state.tree.getNode(this.state.tree.allData, id);
    if (!item) return;

    if (expanded === null) expanded = !item.expanded;

    this.state.tree.setValue(id, "expanded", expanded);

    this.dispatch("setActiveItem", { id, itemId });
    this.dispatch("calculateVisibleItemIds");
    this.dispatch("resetActiveContextMenu", null, { root: true });
    this.dispatch("recalculateHashTable");
  }
  async addNode({ teamId, node, email, basePath }) {
    try {
      const payload = {
        teamId,
        node,
        email,
      };
      await api.patch(`/${basePath}/addNode`, payload);
    } catch (error) {
      console.error("Error adding node:", error);
    }
  }

  async renameNode({ nodeId, newTitle, basePath }) {
    try {
      const payload = { nodeId, newTitle };
      await api.patch(`/${basePath}/renameNode`, payload);
    } catch (error) {
      console.error("Error renaming node:", error);
    }
  }

  async removeNode({ nodeId, teamId, email, basePath }) {
    try {
      const payload = { nodeId, teamId, email };
      await api.patch(`/${basePath}/deleteNode`, payload);
    } catch (error) {
      console.error("Error removing node:", error);
    }
  }
  async resendInvitation({ designId, nodeId, basePath }) {
    try {
      // Fetch current design permissions
      const design = await this.dispatch("pdesigns/getPermissions", designId, { root: true });

      if (!design || !design.data) {
        console.error("Design details not found.");
        return;
      }

      // Extract required design data
      const { title, type, team_id, permissions } = design.data;

      // Find the specific permission to resend
      const node = permissions.find((perm) => perm.id === nodeId);
      if (!node) {
        console.warn("User permission not found.");
        return;
      }

      // Determine email/teamId based on ID format
      let email = null;
      let teamId = null;

      if (node.id.includes("@")) {
        teamId = TreeDefaultNodeIds.Private;
        email = node.id;
      } else {
        teamId = node.id;
        email = null;
      }

      // Prepare node data
      const newNodeId = uuidv4();
      const additionalData = {
        id: newNodeId,
        itemId: designId,
        title,
        type,
        team_id,
        children: [],
        expanded: true,
        shared: true,
      };

      // Add the node back
      await this.addNode({ teamId, node: { ...additionalData }, email, basePath });
    } catch (error) {
      console.error("Error resending invitation:", error);
    }
  }

  async syncPermissions({ designId, updatedPermissions, basePath }) {
    try {
      // Fetch current permissions from the API
      const design = await this.dispatch("pdesigns/getPermissions", designId, { root: true });
      const permissions = design.data.permissions;

      // Update the process permissions
      await this.dispatch(
        "pdesigns/updateProcessPermissions",
        {
          designId,
          permissions: updatedPermissions,
        },
        { root: true }
      );

      // Compare local and remote nodes
      const { added, removed } = compareNodes(updatedPermissions, permissions);

      const newNodeId = uuidv4();
      const additionalData = {
        id: newNodeId,
        itemId: designId,
        title: design.data.title,
        type: design.data.type,
        team_id: design.data.team_id,
        children: [],
        expanded: true,
        shared: true,
      };

      // Process additions
      for (const node of added) {
        let email = null;
        let teamId = null;

        if (node.id.includes("@")) {
          teamId = TreeDefaultNodeIds.Private;
          email = node.id;
        } else {
          teamId = node.id;
          email = null;
        }

        await this.addNode({ teamId, node: { ...additionalData }, email, basePath });
      }

      // Process removals
      for (const node of removed) {
        let email = null;
        let teamId = null;

        if (node.id.includes("@")) {
          teamId = TreeDefaultNodeIds.Private;
          email = node.id;
        } else {
          teamId = node.id;
          email = null;
        }

        await this.removeNode({ nodeId: newNodeId, teamId, email, basePath });
      }
    } catch (error) {
      console.error("Error syncing permissions:", error);
    }
  }
}
